import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { MAX_TASK_STEP_COUNT, MAX_NUM_TASK_LINKS, CROSS_BASELINE_SEARCH_URL_PARAMS } from '../../constants/constants';
import { RouteWithParams } from '../../helpers/routing';
import Routes from '../../constants/routes';
import { TruncateText } from '../../helpers/strings';
import InputText from '../Common/InputText';
import TextArea from '../Common/TextArea';
import Toggle from '../Common/Toggle/Toggle';
import MultiSelectBox from '../Common/MultiSelectBox/MultiSelectBox';
import Icon from '../Common/Icon';
import DatePicker from '../Common/DatePicker/DatePicker';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import './BaselineTaskForm.scss';
import BaselineTaskDrawer from './BaselineTaskDrawer';
import WorkflowSelector from '../BaselineTaskList/WorkflowSelector/WorkflowSelector';


// tried moving this to scss but it just gets overridden from within the component
const getItemStyle = (isDragging, draggableStyle) => ({
	userSelect: 'none',
	lineHeight: 1,
	margin: `0`,
	background: 'white',
	border: isDragging ? '2px solid #03a9f4' : '',
	// styles we need to apply on draggables
	...draggableStyle,
});

const getListStyle = (isDraggingOver) => ({
	background: isDraggingOver ? '#eee' : 'white',
	borderRadius: '0.25rem',
	padding: 8,
});

export default class BaselineTaskForm extends Component {
	constructor(props, context) {
		super(props, context);

		this.state = {
			isRelationshipDrawerOpen: false,
			relationshipBaselineTaskId: null,
			showMore: false,
			markAsDependent: this.props.task.contingentId ? true : false,
			linkAsPrereq: this.props.task.prereqTasks && this.props.task.prereqTasks.length > 0 ? true : false,
			taskSearchTerm: '',
			targetWeeks: 0,
			targetDays: 0,
			startWeeks: 0,
			startDays: 0,
			eventId: null,
			selectedWorkflows: this.props.task.workflows,
		};
		this.onDragEnd = this.onDragEnd.bind(this);
	}

	onFormChange = (e) => {
		this.props.onFormChange(e.target.name, e.target.value);
	};

	onToggleChange = (name, value) => {
		this.props.onFormChange(name, value);
	};

	onDropdownChange = (id, name) => {
		this.props.onFormChange(name, id);
	};

	onSuccessorAdd = (id) => {
		// when a contingent is selected
		// for some reason calling onFormChange twice here didn't update the object in the controller correctly
		const contingentName = this.props.taskSearchResults.find((m) => m.id === id).name;
		this.props.onContingentChange(id, contingentName);

		this.setState({ markAsDependent: false });
	};

	onSuccessorRemove = () => {
		this.props.onContingentRemove();
	};

	onTaskSearchKeyDown = (e) => {
		if (e.key === 'Enter' || e.keyCode === 13) {
			e.preventDefault(); // Always prevent default
			const value = e.target.value;
			if (value && value.length >= 3) {
				this.props.taskSearch(value);
			}
		}
	};

	onPrereqSearchKeyDown = (e) => {
		const key = e.key || e.keyCode;
		if (key === 'Enter' || key === 13) {
			e.preventDefault();
			const value = e.target.value || e.target.defaultValue;
			if (value?.length >= 3) {
				this.props.taskPrereqSearch(value);
			}
		}
	};
	

	onDatePickerChange = (date, name) => {
		this.props.onFormChange(name, date);
	};

	onMakeMilestone = () => {
		this.props.onFormChange('isMilestone', !this.props.task.isMilestone);
	};

	showMore = () => {
		this.setState({ showMore: !this.state.showMore });
	};

	addLink = () => {
		let links = this.props.task.links.slice();
		if (links.length < MAX_NUM_TASK_LINKS) {
			links.push({
				id: 0,
				name: '',
				url: '',
				isActive: true,
				ordinal: links.length,
				isFile: false,
			});
			this.props.onFormChange('links', links);
		}
	};

	addFileLink = () => {
		let links = this.props.task.links.slice();
		if (links.length < MAX_NUM_TASK_LINKS) {
			links.push({
				id: 0,
				name: '',
				url: '',
				isActive: true,
				ordinal: links.length,
				isFile: true,
			});
			this.props.onFormChange('links', links);
		}
	};

	onMarkAsDependent = () => {
		this.setState((prevState) => ({ markAsDependent: !prevState.markAsDependent }));
	};

	onLinkAsPrereq = () => {
		this.setState((prevState) => ({ linkAsPrereq: !prevState.linkAsPrereq }));
	};

	onAddStaticDates = () => {
		this.props.onFormChange('showStaticDates', !this.props.task.showStaticDates);
	};

	showRelationshipDrawer = (baselineTaskId) => {
		this.setState({ relationshipBaselineTaskId: baselineTaskId, isRelationshipDrawerOpen: true });
	};

	onRelationshipDrawerComplete = () => {
		this.setState({ relationshipBaselineTaskId: null, isRelationshipDrawerOpen: false });
	};

	onAddStep = () => {
		const { task, onFormChange } = this.props;

		const steps = [...task.steps];

		const nextOrdinal = steps.length > 0 ? steps[steps.length - 1].ordinal + 1 : 1;

		const newStep = {
			id: 0,
			name: '',
			isActive: false,
			ordinal: nextOrdinal,
			baselineTaskId: task.id,
		};

		steps.push(newStep);

		onFormChange('steps', steps);
	};

	onRemoveStep = (step) => {
		const steps = this.props.task.steps.slice();
		const index = steps.findIndex((x) => x.ordinal === step.ordinal);
		// removing it from this array will mark it IsActive = false on the server during save (if it existed in the db)
		steps.splice(index, 1);
		// update the ordinal of any other steps after it in the array
		for (let i = index; i < steps.length; i++) {
			steps[i].ordinal = i + 1;
		}
		this.props.onFormChange('steps', steps);
	};

	onUpdateStepName = (step, e) => {
		let steps = this.props.task.steps.slice();
		const index = steps.findIndex((x) => x.ordinal === step.ordinal);
		step.name = e.target.value;
		steps.splice(index, 1, step);
		this.props.onFormChange('steps', steps);
	};

	onDragEnd(result) {
		if (!result.destination) {
			return;
		}

		let steps = [...this.props.task.steps.map(step => ({ ...step }))];

		const [movedStep] = steps.splice(result.source.index, 1);
		steps.splice(result.destination.index, 0, movedStep);

		steps = steps.map((step, index) => ({
			...step,
			ordinal: index,
		}));

		this.props.onFormChange('steps', steps);
	}


	isCertificationToggleDisabled = () => {
		if (!this.props.formFilters.certifiedTaskId) {
			return false;
		} else {
			if (this.props.formFilters.certifiedTaskId != this.props.task.id) {
				return true;
			} else {
				return false;
			}
		}
	};

	onLinkChange = (link, index, field, event) => {
		let links = this.props.task.links.slice();
		link[field] = event.target.value;
		links.splice(index, 1, link);
		this.props.onFormChange('links', links);
	};

	onFileLinkChange = (link, index, url) => {
		const selectedFile = this.props.formFilters.subscriptionFiles.find((m) => m.url === url);

		let links = this.props.task.links.slice();
		link.name = selectedFile.name;
		link.url = url;
		link.customerFileId = selectedFile.id;
		links.splice(index, 1, link);
		this.props.onFormChange('links', links);
	};

	onSameNameClick = () => {
		RouteWithParams(
			Routes.CrossBaselineSearch,
			[
				{
					key: CROSS_BASELINE_SEARCH_URL_PARAMS.name,
					value: this.props.task.name.trim(),
				},
			],
			true
		);
	};

	componentDidUpdate(prevProps) {
		// Check if a new step has been added
		if (this.props.task.steps.length !== prevProps.task.steps.length) {
			// Focus on the input element after it's been inserted into the DOM
			const step = document.getElementById(`stepname${this.props.task.steps.length - 1}`);
			step?.focus()
		}

		if (this.props.task.eventId !== prevProps.task.eventId) {
			this.props.onFormChange('actionId', undefined);
		}

		if (this.props.task.actionId !== prevProps.task.actionId) {
			this.props.onFormChange('workflowEmails', []);
		}
	}

	onWorkflowChange = (workflows) => {
		this.setState({ selectedWorkflows: workflows });
		this.props.onFormChange('workflows', workflows);
	};

	handleNumericInputChange = (fieldName) => (e) => {
		const val = e.target.value;
		this.props.onFormChange(fieldName, val === '' ? null : Number(val));
	};
	

	render() {
		const milestoneLinkText = this.props.task.isMilestone ? 'Change to Task' : 'Change to Milestone';
		const milestoneInputText = this.props.task.isMilestone ? 'Milestone Task Name' : 'Task Name';
		const disableCertificationToggle = this.isCertificationToggleDisabled();

		const priorToLabel = this.props.formFilters.isProjectWidgetActive ? 'prior to Zero Date' : 'prior to opening';
		const sameNameLabel =
			this.props.task.sameNameCount > 1
				? `There are ${this.props.task.sameNameCount} additional active baseline tasks with this same name.`
				: `There is ${this.props.task.sameNameCount} additional active baseline task with this same name.`;

		return (
			<div className='baseline-task-form'>
				<form noValidate className={this.props.isFormValid ? '' : 'was-validated'}>
					{this.props.task.sameNameCount && this.props.task.sameNameCount > 0 ? (
						<div className='alert alert-warning text-center'>
							<span>{sameNameLabel}</span>
							<span className='alert-icon ms-2' onClick={this.onSameNameClick}>
								<Icon name='external-link-alt' />
							</span>
						</div>
					) : null}
					{this.props.task.contingentId && (
						<div className='alert alert-secondary contingent-alert'>
							<div className='contingent-alert-link'>
								This task is a prerequisite of:{' '}
								<a href='#' onClick={() => this.showRelationshipDrawer(this.props.task.contingentId)}>
									{TruncateText(this.props.task.contingentName, 20)}
								</a>
							</div>
							<div className='contingent-alert-remove'>
								<button type='button' className='remove-dependent btn btn-link' onClick={this.onSuccessorRemove}>
									Remove dependency
								</button>
							</div>
						</div>
					)}
					{this.props.task.prereqTasks && this.props.task.prereqTasks.length > 0 && (
						<div className='alert alert-secondary contingent-alert'>
							<div>
								This task has the following pre-requisites:
								{this.props.task.prereqTasks.map((prereq) => {
									return (
										<div className='col-12' key={prereq.id}>
											<span className='me-2 remove' onClick={() => this.props.onRemovePrereq(prereq.id)}>
												<Icon name='times' />
											</span>
											<a href='#' onClick={() => this.showRelationshipDrawer(prereq.id)}>
												{TruncateText(prereq.name, 80)}
											</a>
										</div>
									);
								})}
							</div>
						</div>
					)}
					<div className='row'>
						<div className='col-sm-6'>
							<div className='row'>
								<div className='col-12 milestone text-end'>
									<button type='button' className='btn btn-link' onClick={this.onMakeMilestone}>
										{milestoneLinkText}
									</button>
								</div>
							</div>
							<TextArea
								rows={2}
								name='name'
								label={milestoneInputText}
								placeholder={milestoneInputText}
								value={this.props.task.name}
								isRequired={true}
								errorMessage='Name is required'
								onChange={this.onFormChange}
							/>
							<TextArea
								rows={2}
								name='additionalInfo'
								placeholder='Additional Info'
								label='Additional Info'
								value={this.props.task.additionalInfo}
								onChange={this.onFormChange}
							/>
							<div className='mb-3'>
								<Toggle name='isActive' text='Active' checked={this.props.task.isActive} onChange={this.onToggleChange} />
							</div>
							{!this.props.task.isActive && (
								<div>
									<Toggle
										name='isLocationTaskListActive'
										text='Visible in Location Task List'
										checked={this.props.task.isLocationTaskListActive}
										onChange={this.onToggleChange}
									/>{' '}
									<br />
								</div>
							)}

							<MultiSelectBox
								name='jobTitleId'
								label='Baseline Job Titles'
								options={this.props.formFilters.baselineJobTitles ?? []}
								idField='id'
								valueField='name'
								selectedValues={this.props.task.jobTitleId.toString()}
								onChange={this.onDropdownChange}
								isRequired={true}
								errorMessage='Baseline Job Title is required'
								isMulti={false}
							/>

							<MultiSelectBox
								name='departmentId'
								label='Department'
								options={this.props.formFilters.departments ?? []}
								idField='id'
								valueField='name'
								selectedValues={this.props.task.departmentId.toString()}
								onChange={this.onDropdownChange}
								isRequired={true}
								errorMessage='Department is required'
								isMulti={false}
							/>

							<MultiSelectBox
								name='communityId'
								label='Community'
								options={this.props.formFilters.communities ?? []}
								idField='id'
								valueField='name'
								selectedValues={this.props.task.communityId.toString()}
								onChange={this.onDropdownChange}
								isRequired={true}
								errorMessage='Community is required'
								isMulti={false}
							/>

							{this.state.showMore ? (
								<div>
									<MultiSelectBox
										name='taskTagIds'
										label='Task Tags'
										options={this.props.formFilters?.taskTags ?? []}
										idField='id'
										valueField='name'
										selectedValues={this.props.task.taskTagIds.toString()}
										onChange={this.onDropdownChange}
										isMulti={true}
									/>
									<MultiSelectBox
										name='phaseId'
										label='Phase'
										options={this.props.formFilters?.phases ?? []}
										idField='id'
										valueField='name'
										selectedValues={this.props.task.phaseId.toString()}
										onChange={this.onDropdownChange}
										isMulti={false}
									/>
									<div className='mt-2'>
										{this.props.task.links.map((link, index) => {
											return (
												<table className='links' key={link.ordinal}>
													<tbody>
														{link.isFile ? (
															<tr>
																<td>
																	<MultiSelectBox
																		name='linkFile'
																		label='Files'
																		options={this.props.formFilters?.subscriptionFiles ?? []}
																		idField='url'
																		valueField='name'
																		selectedValues={link.url ? link.url : ''}
																		onChange={(e) => this.onFileLinkChange(link, index, e)}
																		isMulti={false}
																	/>
																</td>
															</tr>
														) : (
															<tr>
																<td>
																	<InputText
																		name='linkUrl'
																		label='Link URL'
																		type='text'
																		placeholder='Link URL'
																		value={link.url}
																		onChange={(e) => this.onLinkChange(link, index, 'url', e)}
																		maxLength={500}
																	/>
																</td>
																<td className='td-spacer'></td>
																<td>
																	<InputText
																		name='linkName'
																		label='Link Abbreviation'
																		type='text'
																		placeholder='Link Abbreviation'
																		value={link.name}
																		onChange={(e) => this.onLinkChange(link, index, 'name', e)}
																		isRequired={link.url !== ''}
																		errorMessage='Abbreviation Required'
																		maxLength={50}
																	/>
																</td>
															</tr>
														)}
													</tbody>
												</table>
											);
										})}
										{this.props.task.links.length < MAX_NUM_TASK_LINKS && (
											<div className='link-buttons'>
												<button type='button' className='btn btn-link remove-mt' onClick={this.addLink}>
													Add Link
												</button>
												{this.props.formFilters.subscriptionFiles && this.props.formFilters.subscriptionFiles.length > 0 && (
													<Fragment>
														<span>|</span>
														<button type='button' className='btn btn-link remove-mt ms-2' onClick={this.addFileLink}>
															Add Link from Baseline Files
														</button>
													</Fragment>
												)}
											</div>
										)}
									</div>
									<div className='mt-2'>
										<table>
											<tbody>
												<tr>
													<td>
														<InputText
															name='contactName'
															label='Contact Name'
															type='text'
															placeholder='Contact Name'
															value={this.props.task.contactName}
															onChange={this.onFormChange}
														/>
													</td>
													<td className='td-spacer'></td>
													<td>
														<InputText
															name='contactEmail'
															label='Contact Email'
															type='email'
															placeholder='Contact Email'
															value={this.props.task.contactEmail}
															onChange={this.onFormChange}
														/>
													</td>
												</tr>
											</tbody>
										</table>
									</div>
									<div className='mb-3'>
										{this.props.formFilters.isProjectWidgetActive && (
											<Toggle
												name='includeCertificationCheckbox'
												text='Include Certification Checkbox'
												checked={this.props.task.includeCertificationCheckbox}
												onChange={this.onToggleChange}
												isDisabled={disableCertificationToggle}
											/>
										)}
										{this.props.task.showStaticDates == false && this.props.formFilters.isProjectWidgetActive === false && (
											<Toggle
												name='isLocationTaskDateAdjustable'
												text='Adjust location dates if Opening Date changes'
												checked={this.props.task.isLocationTaskDateAdjustable}
												onChange={this.onToggleChange}
												isDisabled={false}
											/>
										)}
									</div>

									<WorkflowSelector
										workflows={this.props.formFilters.workflows ?? []}
										selectedWorkflows={this.props.task.workflows}
										onChange={this.onWorkflowChange}
										isDisabled={!this.props.task.isBaselineTaskValidationRulesActive}
									/>

									<button className='btn btn-link' onClick={this.showMore}>
										See less
									</button>
								</div>
							) : (
								<button className='btn btn-link remove-mt' onClick={this.showMore}>
									Show more
								</button>
							)}
						</div>
						<div className='col-sm-6'>
							{this.props.task.id !== 0 && this.props.formFilters.isTaskDependenciesActive && (
								<Fragment>
									{!this.props.task.contingentId && (
										<Fragment>
											{this.state.markAsDependent ? (
												<div className='row'>
													<div className='col-12'>
														<button type='button' className='btn btn-link' onClick={this.onMarkAsDependent}>
															Mark as Dependent
														</button>
													</div>
													<div className='col-12'>
														<MultiSelectBox
															name='contingentId'
															label='Successor'
															options={this.props.taskSearchResults ?? []}
															idField='id'
															valueField='name'
															selectedValues=''
															onChange={this.onSuccessorAdd}
															onInputKeyDown={this.onTaskSearchKeyDown}
															onKeyDown={this.onTaskSearchKeyDown}
															isMulti={false}
															placeholder='Please start typing the task name and press enter'
														/>
													</div>
												</div>
											) : (
												<div className='row'>
													<div className='col-12'>
														<button
															name='markAsDependent'
															type='button'
															className='btn btn-link'
															onClick={this.onMarkAsDependent}>
															Mark as Dependent
														</button>
													</div>
												</div>
											)}
										</Fragment>
									)}

									{this.state.linkAsPrereq ? (
										<div className='row'>
											<div className='col-12'>
												<button type='button' className='btn btn-link' onClick={this.onLinkAsPrereq}>
													Link a Pre-Requisite Task
												</button>
											</div>
											<div className='col-12'>
												<MultiSelectBox
													name='prereqTasks'
													label='Pre-Req'
													options={this.props.taskPrereqSearchResults ?? []}
													idField='id'
													valueField='name'
													selectedValues={''}
													onChange={this.props.onPrereqChange}
													onInputKeyDown={this.onPrereqSearchKeyDown}
													onKeyDown={this.onPrereqSearchKeyDown}
													isMulti={false}
													placeholder='Please start typing the task name and press enter'
												/>
											</div>
										</div>
									) : (
										<div className='row'>
											<div className='col-12'>
												<button type='button' className='btn btn-link' onClick={this.onLinkAsPrereq}>
													Link a Pre-Requisite Task
												</button>
											</div>
										</div>
									)}
								</Fragment>
							)}
							{this.props.showDateError ? (
								<div className='alert alert-danger' role='alert'>
									Target date must be after Start Date
								</div>
							) : (
								''
							)}
							{this.props.task.showStaticDates ? (
								<div>
									{this.props.formFilters.isStartDateActive ? (
										<div>
											<div className='row'>
												<div className='col-12'>
													<label>Start Date {priorToLabel}</label>
												</div>
											</div>
											<div className='row'>
												<div className='col-12'>
													<DatePicker
														showIcon
														value={this.props.task.startStaticDate}
														name='startStaticDate'
														label=''
														onChange={this.onDatePickerChange}
													/>
												</div>
											</div>
										</div>
									) : (
										''
									)}

									<div className='row'>
										<div className='col-12'>
											<label>Target Date {priorToLabel}</label>
										</div>
									</div>
									<div className='row'>
										<div className='col-12'>
											<DatePicker
												showIcon
												value={this.props.task.targetStaticDate}
												name='targetStaticDate'
												label=''
												onChange={this.onDatePickerChange}
												isRequired={true}
												errorMessage='Target Date Required'
											/>
										</div>
									</div>
									<div className='row'>
										<div className='col-12'>
											<button type='button' className='btn btn-link remove-mt' onClick={this.onAddStaticDates}>
												Switch to estimated dates
											</button>
										</div>
									</div>
								</div>
							) : (
								<div>
									{this.props.formFilters.isStartDateActive && (
										<div>
											<div className='row'>
												<div className='col-12 mb-2'>
													<label>Start Date - Weeks/Days {priorToLabel}</label>
												</div>
											</div>
											<table>
												<tbody>
													<tr>
														<td>
															<InputText
																name='startWeeks'
																label='Weeks'
																type='number'
																placeholder='Weeks'
																value={this.props.task.startWeeks ?? ''}
																onChange={this.handleNumericInputChange('startWeeks')}
															/>
														</td>
														<td className='td-spacer'></td>
														<td>
														<InputText
															name="startDays"
															label="Days"
															type="number"
															placeholder="Days"
															value={this.props.task.startDays ?? ''}
															onChange={this.handleNumericInputChange('startDays')}
														/>
														</td>
													</tr>
												</tbody>
											</table>
										</div>
									)}

									<div className='row'>
										<div className='col-12 mb-2'>
											<label>Target Date - Weeks/Days {priorToLabel}</label>
										</div>
									</div>
									<table>
										<tbody>
											<tr>
												<td>
													<InputText
														name='targetWeeks'
														label='Weeks'
														type='number'
														placeholder='Weeks'
														value={this.props.task.targetWeeks ? this.props.task.targetWeeks.toString() : ''}
														onChange={this.onFormChange}
														isRequired={true}
														errorMessage='Target Weeks Required'
													/>
												</td>
												<td className='td-spacer'></td>
												<td>
													<InputText
														name='targetDays'
														label='Days'
														type='number'
														placeholder='Days'
														value={this.props.task.targetDays ? this.props.task.targetDays.toString() : ''}
														onChange={this.onFormChange}
														isRequired={true}
														errorMessage='Target Days Required'
													/>
												</td>
											</tr>
										</tbody>
									</table>
									<div className='row'>
										<div className='col-12'>
											<button type='button' className='btn btn-link remove-mt' onClick={this.onAddStaticDates}>
												Switch to static dates
											</button>
										</div>
									</div>
								</div>
							)}

							{this.props.formFilters.isTaskEffortActive ? (
								<div className='row'>
									<div className='col-12 mt-3'>
										<InputText
											name='anticipatedEffort'
											label='Anticipated Effort'
											type='text'
											placeholder='Anticipated Effort'
											value={this.props.task.anticipatedEffort}
											onChange={this.onFormChange}
										/>
									</div>
								</div>
							) : (
								''
							)}
							{this.props.formFilters.isTaskStepsActive ? (
								<div>
									<div className='row'>
										<div className='col-12 mt-2 remove-mb'>
											<label>Task Steps</label>
										</div>
									</div>
									<DragDropContext onDragEnd={this.onDragEnd}>
										<Droppable droppableId='droppable'>
											{(provided, snapshot) => (
												<div ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)}>
													{this.props.task.steps.map((step, index) => (
														<Draggable key={step.ordinal} draggableId={step.ordinal.toString()} index={index}>
															{(provided, snapshot) => (
																<div
																	className='task-step row'
																	ref={provided.innerRef}
																	{...provided.draggableProps}
																	{...provided.dragHandleProps}
																	style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}>
																	<div className='col-1'>
																		<div className='task-step-bars'>
																			<Icon name='bars' />
																		</div>
																	</div>
																	<div className='col-10'>
																		<InputText
																			name={`stepname${index}`}
																			label='Name'
																			type='text'
																			placeholder='Name'
																			value={step.name}
																			onChange={(e) => this.onUpdateStepName(step, e)}
																			isRequired={true}
																			errorMessage='Name Required'
																			maxLength={200}
																		/>
																	</div>
																	<div className='col-1'>
																		<div
																			className='task-step-remove text-center'
																			onClick={() => this.onRemoveStep(step)}>
																			<Icon name='trash-alt' />
																		</div>
																	</div>
																</div>
															)}
														</Draggable>
													))}
													{provided.placeholder}
												</div>
											)}
										</Droppable>
									</DragDropContext>
									{this.props.task.steps.length < MAX_TASK_STEP_COUNT ? (
										<div className='row'>
											<div className='col-12 remove-mt'>
												<button name='addStep' type='button' className='btn btn-link remove-mt' onClick={this.onAddStep}>
													Add a Step
												</button>
											</div>
										</div>
									) : (
										''
									)}
								</div>
							) : (
								''
							)}
						</div>
					</div>
					{this.props.task.id > 0 && (
						<div className='row mt-3'>
							<div className='col-md-6'>
								<InputText
									name='createdByName'
									isDisabled={true}
									label='Created By'
									type='text'
									value={this.props.task.createdByName}
								/>
								<InputText
									name='createdDate'
									isDisabled={true}
									label='Created Date'
									type='text'
									value={this.props.task.dateCreated}
								/>
							</div>
							<div className='col-md-6'>
								<InputText
									name='modifiedByName'
									isDisabled={true}
									label='Modified By'
									type='text'
									value={this.props.task.modifiedByName}
								/>
								<InputText
									name='modifiedDate'
									isDisabled={true}
									label='Modified Date'
									type='text'
									value={this.props.task.dateModified}
								/>
							</div>
						</div>
					)}
				</form>
				{this.state.isRelationshipDrawerOpen ? (
					<BaselineTaskDrawer
						taskId={this.state.relationshipBaselineTaskId}
						baselineId={this.props.task.baselineId}
						onComplete={this.onRelationshipDrawerComplete}
						onCancel={this.onRelationshipDrawerComplete}
						formFilters={this.props.formFilters}
						baselineTaskActions={this.props.baselineTaskActions}
					/>
				) : (
					''
				)}
			</div>
		);
	}
}

BaselineTaskForm.propTypes = {
	task: PropTypes.object.isRequired,
	isFormValid: PropTypes.bool.isRequired,
	showDateError: PropTypes.bool.isRequired,
	onFormChange: PropTypes.func.isRequired,
	onContingentChange: PropTypes.func.isRequired,
	onContingentRemove: PropTypes.func.isRequired,
	onRemovePrereq: PropTypes.func.isRequired,
	onPrereqChange: PropTypes.func.isRequired,
	formFilters: PropTypes.object.isRequired,
	taskSearch: PropTypes.func.isRequired,
	taskPrereqSearch: PropTypes.func.isRequired,
	taskSearchResults: PropTypes.array.isRequired,
	taskPrereqSearchResults: PropTypes.array.isRequired,
	baselineTaskActions: PropTypes.object.isRequired,
};